import { useEffect, useRef } from 'react'

interface AudioVisualizerProps {
  isActive: boolean
  isRecording: boolean
}

export function AudioVisualizer({ isActive, isRecording }: AudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>(0)
  const phaseRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resize = () => {
      canvas.width = canvas.offsetWidth * 2
      canvas.height = canvas.offsetHeight * 2
      ctx.scale(2, 2)
    }
    resize()
    window.addEventListener('resize', resize)

    const draw = () => {
      const w = canvas.offsetWidth
      const h = canvas.offsetHeight
      ctx.clearRect(0, 0, w, h)

      if (!isActive) {
        // Неактивное состояние — мягкая волна
        drawIdleWave(ctx, w, h, phaseRef.current)
        phaseRef.current += 0.02
      } else if (isRecording) {
        // Запись — пульсирующие круги
        drawRecordingCircles(ctx, w, h, phaseRef.current)
        phaseRef.current += 0.05
      } else {
        // Воспроизведение — звуковые волны
        drawSpeakingWaves(ctx, w, h, phaseRef.current)
        phaseRef.current += 0.08
      }

      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      cancelAnimationFrame(animationRef.current)
      window.removeEventListener('resize', resize)
    }
  }, [isActive, isRecording])

  return (
    <div className={`visualizer ${isActive ? 'active' : ''} ${isRecording ? 'recording' : ''}`}>
      <canvas ref={canvasRef} />
    </div>
  )
}

function drawIdleWave(ctx: CanvasRenderingContext2D, w: number, h: number, phase: number) {
  const gradient = ctx.createLinearGradient(0, 0, w, 0)
  gradient.addColorStop(0, 'rgba(255, 107, 53, 0.3)')
  gradient.addColorStop(0.5, 'rgba(255, 107, 53, 0.5)')
  gradient.addColorStop(1, 'rgba(255, 107, 53, 0.3)')

  ctx.beginPath()
  ctx.strokeStyle = gradient
  ctx.lineWidth = 2

  for (let x = 0; x < w; x++) {
    const y = h / 2 + Math.sin(x * 0.02 + phase) * 10 + Math.sin(x * 0.01 + phase * 0.5) * 5
    if (x === 0) ctx.moveTo(x, y)
    else ctx.lineTo(x, y)
  }
  ctx.stroke()
}

function drawRecordingCircles(ctx: CanvasRenderingContext2D, w: number, h: number, phase: number) {
  const cx = w / 2
  const cy = h / 2

  for (let i = 0; i < 3; i++) {
    const radius = 30 + i * 20 + Math.sin(phase + i) * 5
    const alpha = 0.4 - i * 0.1 + Math.sin(phase * 2 + i) * 0.1

    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.strokeStyle = `rgba(255, 71, 87, ${Math.max(0, alpha)})`
    ctx.lineWidth = 2
    ctx.stroke()
  }

  // Центральная точка
  ctx.beginPath()
  ctx.arc(cx, cy, 8 + Math.sin(phase * 3) * 3, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(255, 71, 87, 0.8)'
  ctx.fill()
}

function drawSpeakingWaves(ctx: CanvasRenderingContext2D, w: number, h: number, phase: number) {
  const barCount = 32
  const barWidth = w / barCount - 2
  const gradient = ctx.createLinearGradient(0, h, 0, 0)
  gradient.addColorStop(0, 'rgba(255, 107, 53, 0.2)')
  gradient.addColorStop(0.5, 'rgba(255, 107, 53, 0.6)')
  gradient.addColorStop(1, 'rgba(255, 107, 53, 0.9)')

  for (let i = 0; i < barCount; i++) {
    const x = i * (barWidth + 2) + 1
    const amplitude = Math.sin(phase + i * 0.3) * 0.5 + 0.5
    const barHeight = amplitude * h * 0.8 + 4
    const y = (h - barHeight) / 2

    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.roundRect(x, y, barWidth, barHeight, 2)
    ctx.fill()
  }
}
